'use client'

import { useNavigationGuard } from '@/components/navigation/navigation-guard-context'
import { useSidebar } from '@/hooks/useSidebar'
import { isActivePath } from '@/lib/menu-utils'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import type { ComponentProps, ReactNode } from 'react'

export interface SideMenuProps extends Omit<ComponentProps<'button'>, 'children'> {
  to?: string
  icon?: ReactNode
  title: string
  collapsed: boolean
  end?: boolean
  nested?: boolean
  suffix?: ReactNode
  disabled?: boolean
  isActive?: boolean
  onNavigate?: () => void
}

export function SideMenu({
  to,
  icon,
  title,
  collapsed,
  end,
  nested,
  suffix,
  disabled,
  isActive: isActiveProp,
  onNavigate,
  onClick,
  className: buttonClassName,
  ...buttonProps
}: SideMenuProps) {
  const { requestNavigation } = useNavigationGuard()
  const pathname = usePathname() ?? ''
  const router = useRouter()
  const isActive = isActiveProp ?? (!disabled && !!to && isActivePath(pathname, to, end))
  const { setOpen } = useSidebar()

  const className = cn(
    'group flex h-10 items-center rounded-200 bg-creation-secondary-container-surface-default pl-2 pr-3 py-2 transition-colors',
    disabled
      ? 'pointer-events-none text-creation-secondary-container-on-surface-disable'
      : cn(
          'hover:bg-creation-primary-container-surface-hover hover:text-creation-primary-container-on-surface-hover',
          'active:bg-creation-primary-container-surface-pressed active:text-creation-primary-container-on-surface-pressed',
          isActive
            ? 'bg-creation-primary-container-surface-focused text-creation-primary-container-on-surface-focused'
            : 'text-creation-secondary-container-on-surface-default',
        ),
    collapsed && 'justify-center p-2',
    nested && !collapsed && 'flex-1',
  )
  const content = (
    <>
      {icon && (
        <span
          className={cn(
            'flex size-4 shrink-0 items-center justify-center transition-colors [&>svg]:size-full',
            !disabled &&
              cn(
                isActive
                  ? 'text-creation-primary-container-on-surface-focused'
                  : 'text-creation-secondary-container-on-surface-default',
                'group-hover:text-creation-primary-container-on-surface-hover',
                'group-active:text-creation-primary-container-on-surface-pressed',
              ),
          )}
        >
          {icon}
        </span>
      )}
      {!collapsed && (
        <span className="flex-1 truncate text-comp-button-bt2 leading-comp-button-bt2 font-semibold px-2 animate-in fade-in duration-300 motion-reduce:animate-none">
          {title}
        </span>
      )}
    </>
  )
  const link = to ? (
    <Link
      href={disabled ? '#' : to}
      title={collapsed ? title : undefined}
      aria-disabled={disabled}
      onClick={disabled ? (event) => event.preventDefault() : undefined}
      onNavigate={(event) => {
        if (disabled || to === pathname) return
        event.preventDefault()
        requestNavigation(() => {
          onNavigate?.()
          setOpen(false)
          router.push(to)
        })
      }}
      className={className}
    >
      {content}
      {!collapsed && suffix}
    </Link>
  ) : onClick ? (
    <div className={cn(className, 'relative min-w-0 p-0')}>
      <button
        {...buttonProps}
        type="button"
        disabled={disabled}
        title={title}
        onClick={onClick}
        className={cn(
          className,
          'w-full min-w-0 text-left focus-visible:outline-2',
          !collapsed && suffix && 'pr-9',
          buttonClassName,
        )}
      >
        {content}
      </button>
      {!collapsed && suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 [&_button]:pointer-events-auto [&_a]:pointer-events-auto">
          {suffix}
        </span>
      )}
    </div>
  ) : (
    <div className={className}>
      {content}
      {!collapsed && suffix}
    </div>
  )

  if (nested && !collapsed) {
    return (
      <div className="flex items-stretch pl-3">
        <div className="mr-2 w-px shrink-0 bg-main-border-lv01" />
        {link}
      </div>
    )
  }

  return link
}
